// src/pages/ProblemFramer.jsx
// Structured guided form — no AI needed
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const STEPS = [
  {
    id: "situation",
    label: "Current Situation",
    question: "What is the business challenge you're facing?",
    hint: "Describe what's happening today that isn't working. Be specific.",
    placeholder: "e.g. Our warehouse team manually tracks inventory using spreadsheets, leading to frequent stock discrepancies and delayed shipments...",
    type: "textarea"
  },
  {
    id: "outcome",
    label: "Desired Outcome",
    question: "What does success look like?",
    hint: "Describe the ideal end state. What should be possible once this is solved?",
    placeholder: "e.g. Real-time inventory visibility across all 3 warehouses, reducing stock errors by 90% and cutting order delays in half...",
    type: "textarea"
  },
  {
    id: "tried",
    label: "Previous Attempts",
    question: "What have you already tried?",
    hint: "List any solutions, tools, or approaches you've tested and why they didn't work.",
    placeholder: "e.g. We tried upgrading to a newer ERP system in 2023 but it didn't integrate with our existing WMS...",
    type: "textarea"
  },
  {
    id: "metrics",
    label: "Success Metrics",
    question: "How will you measure success?",
    hint: "What specific KPIs or metrics will prove this challenge has been solved?",
    placeholder: "e.g. 95% inventory accuracy, order processing time under 2 hours, zero manual spreadsheet updates needed...",
    type: "textarea"
  },
  {
    id: "constraints",
    label: "Constraints",
    question: "What constraints must a solution work within?",
    hint: "Think about budget, timeline, compliance requirements, existing tech stack, team size.",
    placeholder: "e.g. Must integrate with SAP, HIPAA compliant, solution must be deployable within 3 months, team of 5 non-technical users...",
    type: "textarea"
  },
  {
    id: "stakeholders",
    label: "Stakeholders",
    question: "Who is affected and who will evaluate solutions?",
    hint: "Who experiences this challenge daily? Who has budget authority? Who signs off on the solution?",
    placeholder: "e.g. Operations team of 20 people, CFO approves budget, CTO evaluates technical fit, COO makes final decision...",
    type: "textarea"
  },
  {
    id: "goals",
    label: "Business Goals",
    question: "What are your next year / short term business goals and what's blocking them?",
    hint: "Share your strategic priorities and the obstacles currently preventing you from achieving them.",
    placeholder: "e.g. We aim to expand into 3 new markets by Q3 but our current supply chain visibility is too limited to scale without major errors...",
    type: "textarea"
  },
  {
    id: "repetitive",
    label: "Repetitive Tasks",
    question: "What repetitive tasks does your team experience?",
    hint: "List the manual, time-consuming, or error-prone tasks your team does repeatedly that technology could help with.",
    placeholder: "e.g. Our finance team manually reconciles invoices every week, our ops team re-enters data between 3 systems daily, reports are manually compiled every month...",
    type: "textarea"
  },
  {
    id: "details",
    label: "Challenge Details",
    question: "Final details for your posting",
    hint: "These fields will appear on your public challenge listing.",
    type: "details"
  }
];

const INDUSTRIES = ["AI & Automation", "Sustainability", "Supply Chain", "FinTech", "Healthcare", "Logistics", "Retail Tech", "Cybersecurity", "Other"];
const BUDGETS = ["<$50k", "$50k–$150k", "$150k–$500k", "$500k+", "Negotiable"];
const TIMELINES = ["<1 month", "1–3 months", "3–6 months", "6+ months"];

function generateTitle(answers) {
  const situation = answers.situation || "";
  const words = situation.split(" ").slice(0, 8).join(" ");
  return words ? words + (situation.split(" ").length > 8 ? "..." : "") : "Business Challenge";
}

function generateDescription(answers) {
  const parts = [];
  if (answers.situation) parts.push(answers.situation);
  if (answers.outcome) parts.push(`The goal is to ${answers.outcome.toLowerCase().startsWith("to ") ? answers.outcome.slice(3) : answers.outcome}`);
  return parts.join(" ");
}

export default function ProblemFramer() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({
    situation: "", outcome: "", tried: "", metrics: "",
    constraints: "", stakeholders: "", goals: "", repetitive: "",
    title: "", industry: "", budget: "", timeline: "", tags: "", tagInput: ""
  });
  const [tagList, setTagList] = useState([]);

  const totalSteps = STEPS.length;
  const progress = currentStep >= 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  function next() {
    if (currentStep < totalSteps - 1) setCurrentStep(s => s + 1);
  }
  function back() {
    if (currentStep > 0) setCurrentStep(s => s - 1);
    else setCurrentStep(-1);
  }

  function addTag(val) {
    const t = (val || answers.tagInput).trim();
    if (t && !tagList.includes(t) && tagList.length < 8) {
      setTagList(prev => [...prev, t]);
      setAnswers(a => ({ ...a, tagInput: "" }));
    }
  }
  function removeTag(tag) { setTagList(prev => prev.filter(t => t !== tag)); }

  function handleSubmit() {
    const autoTitle = answers.title || generateTitle(answers);
    const autoDesc = generateDescription(answers);
    const brief = {
      title: autoTitle,
      description: autoDesc,
      industry: answers.industry,
      budget: answers.budget,
      timeline: answers.timeline,
      tags: tagList,
      additionalContext: [
        answers.tried ? `What we've tried: ${answers.tried}` : "",
        answers.metrics ? `Success metrics: ${answers.metrics}` : "",
        answers.constraints ? `Constraints: ${answers.constraints}` : "",
        answers.stakeholders ? `Stakeholders: ${answers.stakeholders}` : "",
        answers.goals ? `Business goals & blockers: ${answers.goals}` : "",
        answers.repetitive ? `Repetitive tasks: ${answers.repetitive}` : ""
      ].filter(Boolean).join("\n\n")
    };
    navigate("/post-need", { state: { prefill: brief } });
  }

  const canProceed = () => {
    const step = STEPS[currentStep];
    if (!step) return false;
    if (step.type === "details") return answers.industry && answers.budget && answers.timeline;
    return answers[step.id]?.trim().length > 10;
  };

  // ── Intro screen ──
  if (currentStep === -1) {
    return (
      <div className="app-shell app-shell--form">
        <div className="app-form-canvas">
          <div style={{
            width: 68, height: 68, borderRadius: 18, margin: "0 0 28px",
            background: "linear-gradient(135deg, #2563d4, #1a3a8f)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>

          <h1 style={{ fontFamily: "Barlow, sans-serif", fontSize: 34, fontWeight: 800, color: "var(--text)", marginBottom: 12, textTransform: "uppercase" }}>
            Challenge Framer
          </h1>
          <p className="app-page-subtitle" style={{ marginBottom: 36, maxWidth: "none" }}>
            Answer 6 quick questions to define your challenge clearly. A well-framed challenge gets better startup pitches — faster.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 40 }}>
            {[
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563d4" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>, title: "Clear scope", desc: "Boundaries so startups pitch relevant solutions" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563d4" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>, title: "Better matches", desc: "Structured briefs attract quality pitches" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563d4" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, title: "Faster results", desc: "Less back-and-forth with misaligned startups" }
            ].map(item => (
              <div key={item.title} className="card" style={{ padding: "22px 16px", textAlign: "left" }}>
                <div style={{ display: "flex", marginBottom: 10 }}>{item.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 5 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => setCurrentStep(0)} style={{
              fontSize: 15, padding: "13px 36px", borderRadius: 99,
              background: "linear-gradient(135deg, #2563d4, #1a3a8f)", color: "#fff",
              border: "none", cursor: "pointer", fontWeight: 600
            }}>Start framing → (6 questions)</button>
            <button onClick={() => navigate("/post-need")} style={{
              fontSize: 15, padding: "13px 24px", borderRadius: 99,
              background: "transparent", color: "var(--text)",
              border: "1.5px solid var(--border-strong)", cursor: "pointer"
            }}>Skip to form</button>
          </div>
        </div>
      </div>
    );
  }

  const step = STEPS[currentStep];

  return (
    <div className="app-shell app-shell--form">
      <div className="app-form-canvas" style={{ minHeight: "calc(100dvh - 120px)" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "var(--text)", fontFamily: "Barlow, sans-serif", textTransform: "uppercase" }}>Challenge Framing</h1>
          <p style={{ fontSize: 13, color: "var(--text-3)" }}>Step {currentStep + 1} of {totalSteps}</p>
        </div>
        <button onClick={() => navigate("/post-need")} style={{ fontSize: 13, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer" }}>
          Skip to form →
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: "var(--surface-2)", borderRadius: 99, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg, #2563d4, #75faaa)", borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>

      {/* Step pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 32, flexWrap: "wrap" }}>
        {STEPS.map((s, i) => (
          <div key={s.id} style={{
            padding: "4px 12px", borderRadius: 99, fontSize: 11, fontWeight: 500,
            background: i === currentStep ? "rgba(37,99,212,0.2)" : i < currentStep ? "rgba(117,250,170,0.1)" : "var(--surface-2)",
            color: i === currentStep ? "#60a5fa" : i < currentStep ? "var(--teal)" : "var(--text-3)",
            border: i === currentStep ? "1px solid rgba(37,99,212,0.3)" : i < currentStep ? "1px solid rgba(117,250,170,0.2)" : "1px solid transparent"
          }}>
            {i < currentStep ? "✓ " : ""}{s.label}
          </div>
        ))}
      </div>

      {/* Question card */}
      <div className="card" style={{ padding: "32px", marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
          Question {currentStep + 1} of {totalSteps}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", marginBottom: 8, letterSpacing: "-0.01em" }}>
          {step.question}
        </h2>
        <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 24, lineHeight: 1.6 }}>{step.hint}</p>

        {step.type === "textarea" && (
          <textarea
            className="textarea"
            style={{ minHeight: 130 }}
            value={answers[step.id]}
            onChange={e => setAnswers(a => ({ ...a, [step.id]: e.target.value }))}
            placeholder={step.placeholder}
            autoFocus
          />
        )}

        {step.type === "details" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Challenge title <span style={{ color: "var(--coral-text)" }}>*</span></label>
              <input className="input" value={answers.title || generateTitle(answers)} onChange={e => setAnswers(a => ({ ...a, title: e.target.value }))} placeholder="Give your challenge a short, clear title" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Industry <span style={{ color: "var(--coral-text)" }}>*</span></label>
                <select className="select" value={answers.industry} onChange={e => setAnswers(a => ({ ...a, industry: e.target.value }))}>
                  <option value="">Select</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Budget <span style={{ color: "var(--coral-text)" }}>*</span></label>
                <select className="select" value={answers.budget} onChange={e => setAnswers(a => ({ ...a, budget: e.target.value }))}>
                  <option value="">Select</option>
                  {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Timeline <span style={{ color: "var(--coral-text)" }}>*</span></label>
                <select className="select" value={answers.timeline} onChange={e => setAnswers(a => ({ ...a, timeline: e.target.value }))}>
                  <option value="">Select</option>
                  {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tags / keywords</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input className="input" value={answers.tagInput} onChange={e => setAnswers(a => ({ ...a, tagInput: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Type a tag and press Enter" />
                <button type="button" onClick={() => addTag()} style={{
                  padding: "9px 16px", borderRadius: "var(--radius)", background: "var(--surface-2)",
                  border: "1px solid var(--border-strong)", color: "var(--text)", cursor: "pointer", whiteSpace: "nowrap", fontSize: 13
                }}>Add</button>
              </div>
              {tagList.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {tagList.map(t => (
                    <span key={t} style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 10px", borderRadius: 99, background: "var(--teal-light)", color: "var(--teal)", fontSize: 12,
                      border: "1px solid rgba(117,250,170,0.2)"
                    }}>
                      {t}
                      <button onClick={() => removeTag(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--teal)", fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Character count hint */}
      {step.type === "textarea" && answers[step.id].length > 0 && answers[step.id].length < 10 && (
        <div style={{ fontSize: 12, color: "var(--coral-text)", marginBottom: 12 }}>Please add a bit more detail (at least 10 characters)</div>
      )}

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={back} style={{
          padding: "11px 24px", borderRadius: 99, background: "transparent",
          border: "1.5px solid var(--border-strong)", color: "var(--text-2)", cursor: "pointer", fontSize: 14
        }}>← Back</button>

        <div style={{ display: "flex", gap: 8 }}>
          {currentStep < totalSteps - 1 ? (
            <>
              <button onClick={next} disabled={!canProceed()} style={{
                padding: "11px 32px", borderRadius: 99, fontSize: 14, fontWeight: 600, cursor: canProceed() ? "pointer" : "not-allowed",
                background: canProceed() ? "linear-gradient(135deg, #2563d4, #1a3a8f)" : "var(--surface-2)",
                color: canProceed() ? "#fff" : "var(--text-3)", border: "none"
              }}>
                Next →
              </button>
              <button onClick={next} style={{
                padding: "11px 16px", borderRadius: 99, fontSize: 13, cursor: "pointer",
                background: "transparent", color: "var(--text-3)", border: "1px solid var(--border)"
              }}>Skip</button>
            </>
          ) : (
            <button onClick={handleSubmit} disabled={!canProceed()} style={{
              padding: "11px 36px", borderRadius: 99, fontSize: 14, fontWeight: 600, cursor: canProceed() ? "pointer" : "not-allowed",
              background: canProceed() ? "linear-gradient(135deg, #75faaa, #2563d4)" : "var(--surface-2)",
              color: canProceed() ? "#071525" : "var(--text-3)", border: "none"
            }}>
              Generate brief & post challenge →
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
